const numeric = value => typeof value === "number" && Number.isFinite(value) && value >= 0;
const difference = (end, start) => numeric(end) && numeric(start) && end >= start ? end - start : 0;
const categories = { script: "script", css: "stylesheet", img: "image", image: "image", font: "font", fetch: "fetch", xmlhttprequest: "xhr", navigation: "document" };

export function excludedDiagnosticResource(entry, apiOrigin) {
    try {
        const url = new URL(entry.name);
        return !["http:", "https:"].includes(url.protocol) || (url.origin === apiOrigin && /^\/api\/(diagnostics|rest-auth|auth|login|logout|token-auth|user-token|impersonate-token|impersonate-login|user-profile)(\/|$)/i.test(url.pathname));
    } catch (_) { return true; }
}

export function sanitizeResourceTiming(entry, pageOrigin, apiOrigin) {
    let origin = "";
    try { origin = new URL(entry.name).origin; } catch (_) { /* No URL is persisted. */ }
    const sameOrigin = origin === pageOrigin;
    const originCategory = sameOrigin ? "same_origin" : origin === apiOrigin ? "work_api" : origin ? "external" : "unknown";
    const available = sameOrigin || entry.responseStart > 0;
    const result = { label: categories[entry.initiatorType] || "other", origin_category: originCategory, same_origin: sameOrigin, timing_available: available, resource_count: 1, sample_count: available ? 1 : 0, duration_ms: numeric(entry.duration) ? entry.duration : 0 };
    if (available) {
        Object.assign(result, {
            dns_ms: difference(entry.domainLookupEnd, entry.domainLookupStart), connect_ms: difference(entry.connectEnd, entry.connectStart),
            tls_ms: entry.secureConnectionStart > 0 ? difference(entry.connectEnd, entry.secureConnectionStart) : 0,
            ttfb_ms: difference(entry.responseStart, entry.requestStart), download_ms: difference(entry.responseEnd, entry.responseStart),
        });
        for (const [target, source] of [["transfer_size", "transferSize"], ["encoded_size", "encodedBodySize"], ["decoded_size", "decodedBodySize"]]) {
            if (numeric(entry[source])) result[target] = entry[source];
        }
    }
    return result;
}

export function createDiagnosticsPerformance({ navigation, emit, revise, uuid, tabId, now, performanceObject, Observer, pageOrigin, apiOrigin, maxEntries = 300 }) {
    let enabled = false;
    const observed = new Set();
    const observers = [], groups = new Map(), counts = new Map(), announced = new Set();
    const fields = ["duration_ms", "dns_ms", "connect_ms", "tls_ms", "ttfb_ms", "download_ms", "transfer_size", "encoded_size", "decoded_size", "resource_count", "sample_count"];
    function event(kind, values, context = navigation.current()) {
        if (!enabled || !context) return null;
        return emit({ id: uuid(), tab_id: tabId, navigation_id: context.id, revision: 1, kind, page: context.event.page, started_at: new Date(now()).toISOString(), outcome: "completed", ...values });
    }
    function capability(name, supported, label) {
        const context = navigation.current();
        if (!context || announced.has(`${context.id}:${name}`)) return;
        announced.add(`${context.id}:${name}`);
        event("milestone", { label, capability: name, capability_supported: supported });
    }
    function collect(entries) {
        const context = navigation.current();
        if (!enabled || !context) return;
        for (const entry of entries) {
            if (entry.startTime < context.started) continue;
            if (entry.entryType === "resource") {
                // Do not measure our own uploads: each upload would otherwise trigger another upload.
                if (excludedDiagnosticResource(entry, apiOrigin)) continue;
                const safe = sanitizeResourceTiming(entry, pageOrigin, apiOrigin);
                const key = `${context.id}:resource:${safe.label}:${safe.origin_category}`;
                let group = groups.get(key);
                const count = counts.get(context.id) || 0;
                if (count >= maxEntries) {
                    const overflow = `${context.id}:overflow`;
                    let row = groups.get(overflow);
                    if (!row) { row = event("resource", { label: "resource_summary", resource_count: 0, truncated_count: 0 }); groups.set(overflow, row); }
                    if (row) revise(row, { truncated_count: row.truncated_count + 1 });
                    continue;
                }
                counts.set(context.id, count + 1);
                if (!group) { group = event("resource", safe); groups.set(key, group); }
                else {
                    const values = { timing_available: group.timing_available || safe.timing_available };
                    for (const field of fields) if (numeric(safe[field])) values[field] = (group[field] || 0) + safe[field];
                    revise(group, values);
                }
            } else if (entry.entryType === "longtask") {
                const key = `${context.id}:longtask`;
                let group = groups.get(key);
                if (!group) { group = event("longtask", { label: "longtask", longtask_count: 0, longtask_ms: 0, longtask_max_ms: 0, truncated_count: 0 }); groups.set(key, group); }
                if (!group) continue;
                if (group.longtask_count >= maxEntries) revise(group, { truncated_count: group.truncated_count + 1 });
                else revise(group, { longtask_count: group.longtask_count + 1, longtask_ms: group.longtask_ms + entry.duration, longtask_max_ms: Math.max(group.longtask_max_ms, entry.duration) });
            } else if (["paint", "largest-contentful-paint"].includes(entry.entryType)) {
                const label = entry.entryType === "largest-contentful-paint" ? "largest_contentful_paint" : entry.name === "first-contentful-paint" ? "first_contentful_paint" : "first_paint";
                const key = `${context.id}:${label}`;
                if (!groups.has(key)) groups.set(key, event("milestone", { label, duration_ms: entry.startTime - context.started }));
                else if (label === "largest_contentful_paint") revise(groups.get(key), { duration_ms: entry.startTime - context.started });
            }
        }
    }
    function enter() {
        if (!enabled) return;
        capability("resource_timing", observed.has("resource"), "resource_summary");
        capability("longtask", observed.has("longtask"), "longtask");
        capability("paint", observed.has("paint"), "first_paint");
        capability("navigation_timing", !!performanceObject?.getEntriesByType?.("navigation")?.length, "document");
    }
    function documentTiming() {
        if (!enabled) return;
        const context = navigation.current();
        const entry = performanceObject?.getEntriesByType?.("navigation")?.[0];
        if (!context || !entry || context.event.label !== "reload") return;
        const safe = sanitizeResourceTiming({ ...Object.fromEntries(["name", "duration", "responseStart", "domainLookupEnd", "domainLookupStart", "connectEnd", "connectStart", "secureConnectionStart", "requestStart", "responseEnd", "transferSize", "encodedBodySize", "decodedBodySize"].map(key => [key, entry[key]])), initiatorType: "navigation" }, pageOrigin, apiOrigin);
        const key = `${context.id}:document`;
        if (!groups.has(key)) groups.set(key, event("resource", { ...safe, capability: "navigation_timing" }));
        else revise(groups.get(key), safe);
    }
    function stop() { observers.forEach(observer => observer.disconnect()); observers.length = 0; enabled = false; observed.clear(); groups.clear(); counts.clear(); announced.clear(); }
    function start() {
        stop(); enabled = true;
        const types = Observer?.supportedEntryTypes || [];
        for (const type of ["resource", "longtask", "paint", "largest-contentful-paint"]) {
            if (!types.includes(type)) continue;
            try { const observer = new Observer(list => collect(list.getEntries())); observer.observe({ type, buffered: true }); observers.push(observer); observed.add(type); } catch (_) { /* Capability information stays conservative. */ }
        }
        enter(); documentTiming();
    }
    return { start, stop, enter, collect, documentTiming };
}
